import { HttpClient } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FirebaseService } from '@vsolv/packages/firebase/web';
import {
  CustomersCustomerPortalService,
  StatusTypes,
  WarrantyCustomerPortalService,
} from '@wsphere/customer-portal/web';
import { Customer } from '@wsphere/customers/domain';
import { filter, firstValueFrom, map, take, timeout } from 'rxjs';
import { SalesService } from '../../services/sales.service';

enum InitializeStep {
  GET_CUSTOMER = 'Getting Customer',
  GET_WARRANTY = 'Getting Warranty',
  CHECKOUT = 'Redirecting to checkout',
  SIGNOUT = 'Signing out of firebase',
  LOGIN = 'Redirecting to login',
}
@Component({ changeDetection: ChangeDetectionStrategy.Eager, templateUrl: './welcome.page.html', standalone: false })
// eslint-disable-next-line @angular-eslint/component-class-suffix
export class WelcomePage implements OnInit, OnDestroy {
  constructor(
    private firebaseSvc: FirebaseService,
    private router: Router,
    private warrantySvc: WarrantyCustomerPortalService,
    private route: ActivatedRoute,
    private customerSvc: CustomersCustomerPortalService,
    private http: HttpClient,
    private salesSvc: SalesService
  ) {}
  readonly statusTypes = StatusTypes;
  private attempts = 0;
  private lastStep = InitializeStep.GET_CUSTOMER;
  private noWarranties = false;
  private initializeComplete = false;
  private readonly onVisibilityChange = () => {
    this.handleVisibilityChange().catch(err => console.error(err));
  };

  status = StatusTypes.SigningIn;

  async ngOnInit() {
    this.attempts = 0;
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    await this.initialize();
  }

  ngOnDestroy() {
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
  }

  private async handleVisibilityChange() {
    if (document.visibilityState !== 'visible' || !this.initializeComplete || !this.noWarranties) {
      return;
    }

    const salesDisabled = await this.salesSvc.forceRefresh();

    if (salesDisabled) {
      this.status = StatusTypes.SalesDisabled;
    } else if (this.status === StatusTypes.SalesDisabled) {
      this.lastStep = InitializeStep.CHECKOUT;
      await this.router.navigateByUrl('/checkout');
    }
  }

  private async initialize() {
    try {
      await this.waitForFirebaseAuthReady();

      this.lastStep = InitializeStep.GET_CUSTOMER;
      const customer: Customer.Model | undefined =
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        (await firstValueFrom(this.route.parent!.data.pipe(map(data => data['customer'])))) ||
        (await this.customerSvc.retrieveSelf());

      if (customer) {
        this.lastStep = InitializeStep.GET_WARRANTY;
        const warranties = (await this.warrantySvc.list({ page: 1, limit: 1 }))?.items;

        if (warranties?.length) {
          await this.router.navigateByUrl(`/warranty/${warranties[0].contractNumber}`);
        } else {
          this.noWarranties = true;
          const salesDisabled = await this.salesSvc.forceRefresh();
          if (salesDisabled) {
            this.status = StatusTypes.SalesDisabled;
            this.initializeComplete = true;
            return;
          }
          this.lastStep = InitializeStep.CHECKOUT;
          await this.router.navigateByUrl('/checkout');
        }
      } else {
        this.lastStep = InitializeStep.SIGNOUT;
        await this.firebaseSvc.signOut();

        this.lastStep = InitializeStep.LOGIN;
        await this.router.navigateByUrl(`/login`);
      }
      this.initializeComplete = true;
    } catch (err) {
      console.error(err);
      this.attempts += 1;
      if (this.attempts < 5) {
        await new Promise(resolve => setTimeout(resolve, 150 * 5 ** this.attempts));
        await this.initialize();
      } else {
        //post error
        this.status = StatusTypes.FailedToSignIn;
        this.initializeComplete = true;

        let customer = undefined;
        try {
          customer = await this.customerSvc.retrieveSelf();
        } catch {
          //noop
        }
        await firstValueFrom(
          this.http.post('/api/internal_log', {
            message: `Failed to redirect ${
              customer ? 'customer ' + customer.email : 'unknown customer'
            } from welcome page at step: ${this.lastStep}`,
            error: err ? JSON.stringify(err) : undefined,
          })
        );
      }
    }
  }

  private async waitForFirebaseAuthReady() {
    // Prevent protected API calls from racing before Firebase finishes auth hydration.
    await firstValueFrom(
      this.firebaseSvc.getAuthToken$().pipe(
        filter((token): token is string => !!token),
        take(1),
        timeout(5000)
      )
    );
  }
}
